import type { OmaRiistaImportResult } from '@wehunt/ui'
import { apiFetch } from '../../../shared/api/apiFetch'
import { ApiError } from '../../../shared/api/ApiError'

/**
 * Import a hunting area from Oma Riista (the Finnish Wildlife Agency service)
 * by its 10-character public area code (CLUB-190). The backend proxies Tanger
 * to resolve the official boundary, stores it, and writes the hunting ground.
 *
 * Returns an {@link OmaRiistaImportResult}:
 *  - `created` / `refreshed` — imported (or an existing area's boundary refreshed)
 *  - `not-found`             — no area matches the code (HTTP 404)
 *  - `error` + `code`        — any other failure, carrying the backend's stable
 *                              error code (e.g. `oma_riista.login_rejected`) so
 *                              the UI can show it. Non-API (network) errors rethrow.
 */
interface ImportResponse {
  id: string
  status: 'created' | 'refreshed'
}

export async function importOmaRiistaArea(
  groupId: string,
  areaCode: string,
  token: string,
): Promise<OmaRiistaImportResult> {
  try {
    const res = await apiFetch<ImportResponse>(
      `/clubhouse/groups/${encodeURIComponent(groupId)}/hunting-grounds/import/oma-riista`,
      {
        method: 'POST',
        body: JSON.stringify({ areaCode }),
        token,
      },
    )
    return { status: res.status }
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 404) return { status: 'not-found' }
      const code = typeof err.problem.code === 'string' ? err.problem.code : null
      return { status: 'error', code }
    }
    throw err
  }
}
